import { useEffect } from 'react';
import { create } from 'zustand';

import { gravarPreferencia, lerPreferencia } from '../armazenamento/local';

export type Som =
  | 'mensagem'
  | 'mencao'
  | 'dm'
  | 'thread'
  | 'enviada'
  | 'reacao'
  | 'entrada'
  | 'urgente';

/** Um atalho criado pela pessoa (U9): ação + teclas. */
export type AtalhoProprio = {
  id: string;
  acao: 'nao-perturbe' | 'abrir-canal' | 'caixa' | 'marcar-lido';
  /** Canal de "abrir canal". */
  alvo?: string;
  teclas: string;
};

/**
 * Preferências deste aparelho (como o Discord guarda acessibilidade, chat e
 * notificações do computador por aparelho): ficam no navegador. As da conta
 * (tema, compacto, prévias, modo desenvolvedor…) estão nas preferências do
 * servidor (`/users/@me/settings`).
 */
export type Preferencias = {
  // Notificações (U3)
  notificacoesNaTela: boolean;
  /** Sons ligados (U3): mensagem, menção, DM, resposta em thread, enviada, reação, entrada, urgente. */
  sons: Record<Som, boolean>;
  // Privacidade (U6)
  mostrarDigitando: boolean;
  /** Minutos sem mexer até ficar Ausente; 0 = nunca. */
  ausenteApos: number;
  // Aparência (U4)
  avataresNoCompacto: boolean;
  espacoEntreGrupos: number;
  tamanhoFonte: number;
  zoom: number;
  // Acessibilidade (U8)
  reduzirMovimento: 'sistema' | 'sim' | 'nao';
  saturacao: number;
  contrasteAlto: boolean;
  sublinharLinks: boolean;
  coresDeCargo: 'nome' | 'lado' | 'nao';
  lembrarTextoAlternativo: boolean;
  // Chat (U7)
  spoiler: 'clique' | 'gerencio' | 'sempre';
  enterEnvia: boolean;
  corretor: boolean;
  // Atalhos (U9)
  atalhos: AtalhoProprio[];
};

const PADRAO: Preferencias = {
  notificacoesNaTela: true,
  sons: {
    mensagem: true,
    mencao: true,
    dm: true,
    thread: true,
    enviada: false,
    reacao: false,
    entrada: false,
    urgente: true,
  },
  mostrarDigitando: true,
  ausenteApos: 10,
  avataresNoCompacto: false,
  espacoEntreGrupos: 14,
  tamanhoFonte: 15,
  zoom: 100,
  reduzirMovimento: 'sistema',
  saturacao: 100,
  contrasteAlto: false,
  sublinharLinks: false,
  coresDeCargo: 'nome',
  lembrarTextoAlternativo: false,
  spoiler: 'clique',
  enterEnvia: true,
  corretor: true,
  atalhos: [],
};

const CHAVE = 'preferencias';

function lerGuardadas(): Preferencias {
  const guardadas = lerPreferencia<Partial<Preferencias>>(CHAVE, {});
  return {
    ...PADRAO,
    ...guardadas,
    sons: { ...PADRAO.sons, ...(guardadas.sons ?? {}) },
  };
}

export const usePreferencias = create<
  Preferencias & { mudar: (parte: Partial<Preferencias>) => void }
>((set, get) => ({
  ...lerGuardadas(),
  mudar: (parte) => {
    set(parte);
    const { mudar: _mudar, ...tudo } = { ...get(), ...parte };
    gravarPreferencia(CHAVE, tudo);
  },
}));

export function lerPreferencias(): Preferencias {
  return usePreferencias.getState();
}

/**
 * Aplica na página o que é visual: fonte e espaço do chat, zoom, movimento,
 * saturação, contraste e links sublinhados (atributos e variáveis na raiz;
 * o CSS faz o resto).
 */
export function useAplicarPreferencias() {
  const {
    reduzirMovimento,
    saturacao,
    contrasteAlto,
    sublinharLinks,
    tamanhoFonte,
    espacoEntreGrupos,
    zoom,
  } = usePreferencias();
  useEffect(() => {
    const raiz = document.documentElement;
    const reduzido =
      reduzirMovimento === 'sim' ||
      (reduzirMovimento === 'sistema' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    raiz.dataset.movimento = reduzido ? 'reduzido' : 'normal';
    raiz.dataset.contraste = contrasteAlto ? 'alto' : 'normal';
    raiz.dataset.links = sublinharLinks ? 'sublinhados' : 'normal';
    // Filtro só quando reduz: em 100% ele custaria desempenho à toa.
    raiz.dataset.saturacao = saturacao < 100 ? 'reduzida' : 'normal';
    raiz.style.setProperty('--saturacao', String(saturacao / 100));
    raiz.style.setProperty('--fonte-chat', `${tamanhoFonte}px`);
    raiz.style.setProperty('--espaco-grupos', `${espacoEntreGrupos}px`);
    raiz.style.setProperty('zoom', zoom === 100 ? '' : String(zoom / 100));
  }, [
    reduzirMovimento,
    saturacao,
    contrasteAlto,
    sublinharLinks,
    tamanhoFonte,
    espacoEntreGrupos,
    zoom,
  ]);
}
